import React, { useEffect, useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import {
  List,
  MagnifyingGlass,
  House,
  Headphones,
  Video,
  User,
  Bell,
  WifiSlash,
  X,
  ArrowRight,
  Microphone,
  Envelope,
  BookOpen,
  Briefcase,
  Question,
  FileText,
  CaretDown,
  Users,
  Article,
  UsersThree
} from '@phosphor-icons/react';
// Use raster logo.webp for site branding
import logoPath from '../logo.webp';
import { useMedia } from '../context/MediaContext';
import { Footer } from './Footer';
import { MediaPlayer } from './MediaPlayer';
import { searchContent, getArticles, getLatestEpisodes } from '../services/contentService';
import { Article as ArticleType, Episode, ContentType } from '../types';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentEpisode, play } = useMedia();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showSearch, setShowSearch] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<(ArticleType | Episode)[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (searchQuery.trim().length > 1) {
      searchContent(searchQuery).then(setSearchResults);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  useEffect(() => {
    const loadNotifications = async () => {
      const [articles, episodes] = await Promise.all([
        getArticles(undefined, false, 3),
        getLatestEpisodes(3)
      ]);

      const articleItems = articles.map(article => ({
        id: `article-${article.id}`,
        label: 'Breaking News',
        title: article.title,
        time: article.publishedAt ? new Date(article.publishedAt).toLocaleTimeString() : 'Recently',
        color: 'brand'
      }));

      const episodeItems = episodes.map(episode => ({
        id: `episode-${episode.id}`,
        label: 'New Episode',
        title: episode.title,
        time: episode.publishedAt ? new Date(episode.publishedAt).toLocaleTimeString() : 'Recently',
        color: 'blue'
      }));

      setNotifications([...articleItems, ...episodeItems].slice(0, 6));
    };

    loadNotifications();
  }, []);

  // Close menus on navigation
  useEffect(() => {
    setShowMobileMenu(false);
    setShowSearch(false);
    setShowNotifications(false);
  }, [location.pathname]);

  const handleResultClick = (item: ArticleType | Episode) => {
    setShowSearch(false);
    setSearchQuery('');
    // Cast to any to access type discriminator efficiently given the mixed types
    const typedItem = item as any;
    if (typedItem.type === ContentType.Article) {
      const articleItem = item as ArticleType;
      navigate(`/news/${articleItem.category?.toLowerCase() || 'general'}/${articleItem.slug || articleItem.id}`);
    } else {
      play(item as Episode);
    }
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  }

  const isDemoMode = localStorage.getItem('demo_mode') === 'true';

  return (
    <div className="min-h-screen flex flex-col bg-brand-50 selection:bg-brand-500 selection:text-white">
      {isDemoMode && (
        <div className="bg-indigo-600 text-white text-[10px] font-bold uppercase tracking-widest text-center py-2 flex items-center justify-center gap-4 z-50 relative">
          <span>Preview Mode Active</span>
          <button
            onClick={() => { localStorage.removeItem('demo_mode'); window.location.reload(); }}
            className="bg-white/20 hover:bg-white/30 px-2 py-0.5 rounded transition-colors"
          >
            Exit Demo
          </button>
        </div>
      )}

      {!isOnline && (
        <div className="bg-brand-900 text-white text-xs font-bold uppercase tracking-widest text-center py-2 flex items-center justify-center gap-2 z-50 relative">
          <WifiSlash size={14} /> Offline Mode
        </div>
      )}

      <header className="sticky top-0 z-40 bg-brand-900 border-b border-brand-800 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 h-20 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <button onClick={() => setShowMobileMenu(true)} className="md:hidden p-2 -ml-2 text-white hover:text-brand-400 transition-colors">
              <List size={28} weight="bold" />
            </button>
            <Link to="/" className="flex items-center gap-3 group">
              <img src={logoPath} alt="Nida logo" className="w-10 h-auto" />
            </Link>
            <nav className="hidden md:flex gap-6 text-[11px] font-black tracking-widest uppercase text-white/90 ml-8 items-center">
              <Link to="/" className={`hover:text-brand-400 transition-colors flex items-center gap-2 ${isActive('/') ? 'text-brand-400' : ''}`}>
                HOME
              </Link>
              <Link to="/news" className={`hover:text-brand-400 transition-colors flex items-center gap-2 ${isActive('/news') ? 'text-brand-400' : ''}`}>
                NEWS
              </Link>
              <Link to="/news/politics" className={`hover:text-brand-400 transition-colors flex items-center gap-2 ${isActive('/news/politics') ? 'text-brand-400' : ''}`}>
                POLITICS
              </Link>
              <Link to="/news/business" className={`hover:text-brand-400 transition-colors flex items-center gap-2 ${isActive('/news/business') ? 'text-brand-400' : ''}`}>
                BUSINESS
              </Link>
              <Link to="/news/entertainment" className={`hover:text-brand-400 transition-colors flex items-center gap-2 ${isActive('/news/entertainment') ? 'text-brand-400' : ''}`}>
                ENTERTAINMENT
              </Link>
              <Link to="/news/documentary" className={`hover:text-brand-400 transition-colors flex items-center gap-2 ${isActive('/news/documentary') ? 'text-brand-400' : ''}`}>
                DOCUMENTARY
              </Link>
              <Link to="/about" className={`hover:text-brand-400 transition-colors flex items-center gap-2 ${isActive('/about') ? 'text-brand-400' : ''}`}>
                ABOUT US
              </Link>

              {/* Podcast Dropdown */}
              <div className="relative group">
                <button className={`flex items-center gap-1 hover:text-brand-400 transition-colors ${location.pathname.startsWith('/audio') || location.pathname.startsWith('/video') ? 'text-brand-400' : ''}`}>
                  PODCAST <CaretDown size={14} weight="bold" className="group-hover:rotate-180 transition-transform duration-300" />
                </button>
                <div className="absolute top-full left-0 pt-4 opacity-0 translate-y-2 invisible group-hover:opacity-100 group-hover:translate-y-0 group-hover:visible transition-all duration-300 z-50">
                  <div className="bg-brand-900 border border-brand-800 shadow-2xl rounded-xl py-2 w-40 overflow-hidden">
                    <Link to="/audio" className="flex items-center gap-3 px-6 py-3 text-[10px] font-black text-white/80 hover:bg-brand-800 hover:text-brand-400 transition-colors uppercase tracking-widest">
                      <Headphones size={18} weight="duotone" /> Audio
                    </Link>
                    <Link to="/video" className="flex items-center gap-3 px-6 py-3 text-[10px] font-black text-white/80 hover:bg-brand-800 hover:text-brand-400 transition-colors uppercase tracking-widest">
                      <Video size={18} weight="duotone" /> Video
                    </Link>
                  </div>
                </div>
              </div>
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={() => setShowSearch(true)} className="p-2 text-white/70 hover:text-brand-400 hover:bg-white/10 rounded-full transition-all">
              <MagnifyingGlass size={22} weight="bold" />
            </button>
            <button onClick={() => setShowNotifications(!showNotifications)} className="relative p-2 text-white/70 hover:text-brand-400 hover:bg-white/10 rounded-full transition-all">
              <Bell size={22} weight="bold" />
              <span className="absolute top-2 right-2 w-2 h-2 bg-brand-400 rounded-full border border-brand-900"></span>
            </button>
            <Link to="/admin/login" className="w-9 h-9 bg-white/10 rounded-full ml-2 flex items-center justify-center text-white/40 hover:text-brand-400 hover:bg-white/20 transition-all border border-transparent hover:border-brand-400/30">
              <User size={18} weight="bold" />
            </Link>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Navigation */}
      {showMobileMenu && (
        <>
          <div className="fixed inset-0 bg-brand-900/40 z-[55] backdrop-blur-sm transition-opacity animate-fade-in" onClick={() => setShowMobileMenu(false)} />
          <div className="fixed top-0 left-0 bottom-0 w-[85vw] max-w-sm bg-white z-[60] shadow-2xl flex flex-col animate-slide-in-right transform -scale-x-100 overflow-hidden">
            {/* Note: animation logic might just be simpler slide-in */}
            <div className="h-full w-full flex flex-col">
              <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                <div className="flex items-center gap-2">
                  <img src={logoPath} alt="Nida logo" className="w-10 h-auto" />
                </div>
                <button onClick={() => setShowMobileMenu(false)} className="p-2 text-gray-400 hover:text-brand-900 hover:bg-gray-200 rounded-full transition-all"><X size={24} weight="bold" /></button>
              </div>
              <nav className="flex-1 overflow-y-auto p-8 space-y-10">
                <div>
                  <h5 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-6 border-b border-gray-100 pb-2">Network</h5>
                  <div className="space-y-6">
                    <Link to="/" className="flex items-center gap-4 text-xl font-serif font-bold text-brand-900 hover:text-brand-400 transition-colors">
                      <House size={24} weight="duotone" className="text-gray-400" /> HOME
                    </Link>
                    <Link to="/news" className="flex items-center gap-4 text-xl font-serif font-bold text-brand-900 hover:text-brand-400 transition-colors">
                      <Article size={24} weight="duotone" className="text-gray-400" /> NEWS
                    </Link>
                    <Link to="/news/politics" className="flex items-center gap-4 text-xl font-serif font-bold text-brand-900 hover:text-brand-400 transition-colors">
                      <Briefcase size={24} weight="duotone" className="text-gray-400" /> POLITICS
                    </Link>
                    <Link to="/news/business" className="flex items-center gap-4 text-xl font-serif font-bold text-brand-900 hover:text-brand-400 transition-colors">
                      <Briefcase size={24} weight="duotone" className="text-gray-400" /> BUSINESS
                    </Link>
                    <Link to="/news/entertainment" className="flex items-center gap-4 text-xl font-serif font-bold text-brand-900 hover:text-brand-400 transition-colors">
                      <Video size={24} weight="duotone" className="text-gray-400" /> ENTERTAINMENT
                    </Link>
                    <Link to="/news/documentary" className="flex items-center gap-4 text-xl font-serif font-bold text-brand-900 hover:text-brand-400 transition-colors">
                      <BookOpen size={24} weight="duotone" className="text-gray-400" /> DOCUMENTARY
                    </Link>
                    <Link to="/audio" className="flex items-center gap-4 text-xl font-serif font-bold text-brand-900 hover:text-brand-400 transition-colors">
                      <Headphones size={24} weight="duotone" className="text-gray-400" /> NIDA AUDIO
                    </Link>
                    <Link to="/video" className="flex items-center gap-4 text-xl font-serif font-bold text-brand-900 hover:text-brand-400 transition-colors">
                      <Video size={24} weight="duotone" className="text-gray-400" /> NIDA VIDEO
                    </Link>
                    <Link to="/about" className="flex items-center gap-4 text-xl font-serif font-bold text-brand-900 hover:text-brand-400 transition-colors">
                      <UsersThree size={24} weight="duotone" className="text-gray-400" /> ABOUT US
                    </Link>
                  </div>
                </div>
                <div>
                  <h5 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-6 border-b border-gray-100 pb-2">Company</h5>
                  <div className="space-y-4">
                    <Link to="/company/about" className="flex items-center gap-3 text-sm font-bold text-gray-600 hover:text-brand-900"><Users size={18} weight="duotone" /> Our Team</Link>
                    <Link to="/company/contact" className="flex items-center gap-3 text-sm font-bold text-gray-600 hover:text-brand-900"><Envelope size={18} weight="duotone" /> Contact</Link>
                  </div>
                </div>
              </nav>
              <div className="p-6 bg-gray-50 border-t border-gray-100">
                <Link to="/admin/login" className="flex items-center justify-center gap-2 w-full py-4 bg-brand-900 rounded-xl text-sm font-bold text-white shadow-lg shadow-brand-900/10 hover:shadow-brand-900/20 hover:scale-[1.02] transition-all">
                  <User size={18} weight="bold" /> Staff Portal
                </Link>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Notifications Panel */}
      {showNotifications && (
        <div className="absolute top-20 right-4 md:right-12 w-96 bg-white/90 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl z-50 animate-fade-in origin-top-right">
          <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 rounded-t-2xl">
            <h4 className="font-black text-brand-900 text-xs uppercase tracking-widest flex items-center gap-2">
              <Bell size={14} weight="fill" className="text-brand-500" /> Notifications
            </h4>
            <button onClick={() => setShowNotifications(false)} className="hover:bg-gray-200 rounded-full p-1 transition-colors"><X size={14} weight="bold" /></button>
          </div>
          <div className="max-h-[50vh] overflow-y-auto p-2">
            {notifications.length === 0 && (
              <div className="p-6 text-center text-xs font-bold text-gray-400 uppercase tracking-widest">No notifications yet</div>
            )}
            {notifications.map((notif) => (
              <div key={notif.id} className="p-3 hover:bg-gray-50 cursor-pointer rounded-xl transition-colors group">
                <div className="flex gap-3">
                  <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${notif.color === 'blue' ? 'bg-blue-500' : 'bg-brand-500'}`}></div>
                  <div>
                    <p className={`text-[10px] font-bold mb-1 uppercase tracking-widest ${notif.color === 'blue' ? 'text-blue-500' : 'text-brand-500'}`}>{notif.label}</p>
                    <p className="text-sm font-bold text-brand-900 group-hover:text-brand-800 leading-snug">{notif.title}</p>
                    <p className="text-[10px] text-gray-400 mt-1">{notif.time}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search Modal */}
      {showSearch && (
        <div className="fixed inset-0 bg-white/95 backdrop-blur-xl z-[60] flex flex-col animate-fade-in">
          <div className="p-6 md:p-10 flex items-center justify-between border-b border-gray-100">
            <div className="flex items-center gap-6 flex-1 max-w-4xl mx-auto">
              <MagnifyingGlass size={32} weight="light" className="text-brand-500" />
              <input
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for news, podcasts, tags..."
                className="text-3xl md:text-5xl font-serif font-bold w-full focus:outline-none bg-transparent placeholder:text-gray-300 text-brand-900"
              />
            </div>
            <button onClick={() => setShowSearch(false)} className="p-4 hover:bg-gray-100 rounded-full transition-colors"><X size={32} weight="light" /></button>
          </div>
          <div className="flex-1 p-8 max-w-4xl mx-auto w-full overflow-y-auto no-scrollbar">
            {searchResults.length > 0 ? (
              <div className="grid gap-4">
                {searchResults.map(item => (
                  <div
                    key={item.id}
                    onClick={() => handleResultClick(item)}
                    className="group flex gap-6 cursor-pointer p-4 hover:bg-brand-50/50 border border-transparent hover:border-brand-100 rounded-2xl transition-all"
                  >
                    <div className="relative w-32 h-24 shrink-0 rounded-xl overflow-hidden shadow-sm">
                      <img src={(item as any).type === ContentType.Article ? (item as ArticleType).coverImage || '' : (item as Episode).thumbnail || ''} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      {(item as any).type === ContentType.Episode && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                          <div className="bg-white/90 rounded-full p-1.5 backdrop-blur-sm">
                            <Microphone size={12} weight="fill" />
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-black uppercase tracking-widest text-brand-500 bg-brand-50 px-2 py-0.5 rounded-full">{(item as any).type}</span>
                        {(item as any).type === ContentType.Article && (item as ArticleType).category && (
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{(item as ArticleType).category}</span>
                        )}
                      </div>
                      <h4 className="text-xl font-serif font-bold text-brand-900 group-hover:text-brand-500 transition-colors leading-tight">{item.title}</h4>
                      {(item as any).is_featured && <span className="inline-block mt-2 text-[10px] font-bold text-white bg-brand-900 px-2 py-0.5 rounded">FEATURED</span>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-8 animate-fade-up">
                <div>
                  <h5 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-6">Trending Topics</h5>
                  <div className="flex flex-wrap gap-3">
                    {['Climate Policy', 'AI Ethics', 'Urbanism', 'Space Race', 'Global Markets'].map(term => (
                      <button
                        key={term}
                        onClick={() => setSearchQuery(term)}
                        className="px-4 py-2 bg-gray-50 hover:bg-brand-500 hover:text-white rounded-full text-sm font-bold text-gray-600 transition-all duration-300 border border-gray-100 hover:border-brand-500"
                      >
                        #{term}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <main className={`flex-1 w-full max-w-7xl mx-auto px-4 py-8 ${currentEpisode ? 'mb-24' : 'mb-16'}`}>
        {children}
      </main>
      <Footer />
      <MediaPlayer />
    </div>
  );
};